import type { AtLeast, Block, ListView } from '@basehub/data/replicache/index'
import {
  buildYDocFromState,
  richTextExtensionsForTransformations,
  stateToBase64,
  yDocToPlainText,
} from '@basehub/tiptap-api-utils'
import { compareAsc, compareDesc } from 'date-fns'
import { sql } from 'drizzle-orm'
import { BaseFilter } from '@basehub/data/replicache/schema/block/search'

export const filter = ({
  block,
  filter,
  getBlock,
  getChildrenLength,
}: {
  block: AtLeast<Block, 'type' | 'value'>
  filter: BaseFilter
  getBlock: (id: string) => Block | undefined
  getChildrenLength: (id: string) => number
}): boolean => {
  switch (block.type) {
    case 'date': {
      if (!block.value) return true

      switch (filter.type) {
        case 'is-not-empty':
          return !!block.value
        case 'is-empty':
          return !block.value
        case 'is':
          return compareAsc(new Date(block.value), new Date(filter.value)) === 0
        case 'lower-than':
          return compareDesc(new Date(block.value), new Date(filter.value)) >= 0
        case 'greater-than':
          return compareAsc(new Date(block.value), new Date(filter.value)) >= 0
        case 'is-not':
          return compareAsc(new Date(block.value), new Date(filter.value)) !== 0
        default:
          return false
      }
    }
    case 'boolean': {
      switch (filter.type) {
        case 'is':
          return block.value.toString() === filter.value
        default:
          return false
      }
    }
    case 'number':
    case 'color':
    case 'text':
    case 'rich-text':
    case 'list': {
      let string

      if (
        block.type === 'color' ||
        block.type === 'text' ||
        block.type === 'number'
      ) {
        string = block.value
      } else if (block.type === 'rich-text') {
        const base64 = stateToBase64(
          block.value?.state ?? '',
          richTextExtensionsForTransformations
        )
        const yDocA = buildYDocFromState(base64)

        const plainText = yDocToPlainText(
          yDocA,
          richTextExtensionsForTransformations
        )
        string = plainText
      } else {
        string = block.id
          ? getChildrenLength(block.sourceBlockId ?? block.id)
          : 0
      }

      return textFilterAlgorithm({
        filter,
        string: string ?? null,
      })
    }

    case 'select':
    case 'reference': {
      const evaluate =
        block.type === 'select'
          ? block.value.join(',').toLowerCase()
          : block.value.targetBlockIds
              .map((id) => {
                return getBlock(id)?.title
              })
              .join(',')
              .toLowerCase()
      const trimedFilterValue = filter.value
        .split(',')
        .map((v) => v.trim().toLowerCase())
        .join(',')

      switch (filter.type) {
        case 'contains': {
          if (!filter.value) return true
          if (!block.value) return false

          return evaluate.includes(trimedFilterValue)
        }
        case 'is':
        case 'is-not': {
          if (!filter.value) return true
          if (!block.value) return false

          const is = evaluate === trimedFilterValue
          if (filter.type === 'is') return is
          return !is
        }
        case 'ends-with':
        case 'starts-with': {
          if (!filter.value) return true
          if (!block.value) return false

          if (filter.type === 'ends-with') {
            return evaluate.endsWith(trimedFilterValue)
          }
          return evaluate.startsWith(trimedFilterValue)
        }
        case 'is-empty':
        case 'is-not-empty': {
          const isEmpty = !trimedFilterValue
          if (filter.type === 'is-empty') return isEmpty
          return !isEmpty
        }
        default:
          return true
      }
    }

    case 'media': {
      switch (filter.type) {
        case 'lower-than':
        case 'greater-than': {
          if (!filter.value) return true
          if (!block.value) return false
          if (block.value.status !== 'uploaded') return false

          if (filter.type === 'greater-than') {
            return (
              block.value.fileSize / Math.pow(1024, 2) >=
              parseFloat(filter.value)
            )
          }

          return (
            block.value.fileSize / Math.pow(1024, 2) <= parseFloat(filter.value)
          )
        }
        case 'is-not-empty':
        case 'is-empty': {
          if (filter.type === 'is-not-empty') {
            return block.value.status === 'uploaded'
          }

          return block.value.status !== 'uploaded'
        }
        case 'is': {
          if (!filter.value) return true
          if (block.value.status !== 'uploaded') return false

          return block.value.fileName
            .toString()
            .toLowerCase()
            .startsWith(filter.value.toLowerCase())
        }
        case 'contains': {
          if (!filter.value) return true
          if (block.value.status !== 'uploaded') return false

          return block.value.fileName
            .toString()
            .toLowerCase()
            .includes(filter.value.toString().toLowerCase())
        }

        default:
          return false
      }
    }

    default:
      return true
  }
}

const textFilterAlgorithm = ({
  filter,
  string,
}: {
  filter: ListView['filter'][number]
  string: string | number | null
}) => {
  switch (filter.type) {
    case 'is':
    case 'is-not': {
      if (!filter.value) return true
      if (typeof string !== 'string') return false

      const is =
        string.toString().toLowerCase() ===
        filter.value.toString().toLowerCase()

      if (filter.type === 'is') return is
      return !is
    }
    case 'does-not-contain':
    case 'contains': {
      if (!filter.value) return true
      if (typeof string !== 'string') return false

      const contains = string
        .toString()
        .toLowerCase()
        .includes(filter.value.toString().toLowerCase())

      if (filter.type === 'contains') return contains
      return !contains
    }
    case 'starts-with':
    case 'ends-with': {
      if (!filter.value) return true
      if (typeof string !== 'string') return false

      if (filter.type === 'starts-with') {
        return string
          .toString()
          .toLowerCase()
          .startsWith(filter.value.toLowerCase())
      }
      return string
        .toString()
        .toLowerCase()
        .endsWith(filter.value.toLowerCase())
    }
    case 'is-not-empty':
    case 'is-empty': {
      const isEmpty =
        string === null || string === undefined || !string || string === 0
      if (filter.type === 'is-not-empty') return !isEmpty
      return isEmpty
    }

    case 'greater-than':
    case 'lower-than': {
      if (Number.isNaN(parseInt(filter.value))) return true // isn't comparing to a number, act as no filter value was provided
      if (filter.value === null || filter.value === undefined) return true
      if (string === null || string === undefined) return false

      const value = parseFloat(string.toString())

      if (filter.type === 'greater-than') {
        return value > parseFloat(filter.value)
      }

      return value < parseFloat(filter.value)
    }

    default:
      return false
  }
}

export const filterSQL = ({
  filter,
  extract,
}: {
  filter: ListView['filter'][number]
  extract: string
}) => {
  switch (filter.type) {
    case 'is':
      return sql`${sql.raw(extract)} = ${filter.value}`
    case 'is-not':
      return sql`${sql.raw(extract)} != ${filter.value}`
    case 'contains':
      return sql`${sql.raw(extract)} ILIKE ${`%${filter.value}%`}`
    case 'does-not-contain':
      return sql`${sql.raw(extract)} NOT ILIKE ${`%${filter.value}%`}`
    case 'starts-with':
      return sql`${sql.raw(extract)} ILIKE ${`${filter.value}%`}`
    case 'ends-with':
      return sql`${sql.raw(extract)} ILIKE ${`%${filter.value}`}`
    case 'is-empty':
      return sql`(${sql.raw(extract)} IS NULL OR ${sql.raw(
        extract
      )} = '' OR ${sql.raw(extract)} = '[]' OR ${sql.raw(extract)} = '{}')`
    case 'is-not-empty':
      return sql`(${sql.raw(extract)} IS NOT NULL AND ${sql.raw(
        extract
      )} != '' AND ${sql.raw(extract)} != '[]' AND ${sql.raw(extract)} != '{}')`
    case 'greater-than':
      return sql`CAST(${sql.raw(extract)} AS NUMERIC) > ${parseFloat(
        filter.value
      )}`
    case 'lower-than':
      return sql`CAST(${sql.raw(extract)} AS NUMERIC) < ${parseFloat(
        filter.value
      )}`
    default:
      throw new Error(`Unsupported filter type: ${filter.type}`)
  }
}
